import {
  FieldError,
  Input,
  Label,
  Text as AriaText,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { cn } from '../lib/cn';

export interface TextFieldProps extends Omit<AriaTextFieldProps, 'className'> {
  label: string;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  /** Large single-line field for the main input of a sheet ("Was?"). */
  size?: 'md' | 'lg';
  className?: string;
}

/** Labelled text input. Label always visible (no placeholder-as-label), errors announced. */
export function TextField({
  label,
  description,
  errorMessage,
  placeholder,
  size = 'md',
  className,
  ...props
}: TextFieldProps) {
  return (
    <AriaTextField {...props} className={cn('flex flex-col gap-gap-xs', className)}>
      <Label className="text-sm font-semibold text-text-secondary">{label}</Label>
      <Input
        placeholder={placeholder}
        className={cn(
          'w-full rounded-control border border-border-default bg-surface-raised px-inset-sm text-text-primary',
          'placeholder:text-text-muted outline-none',
          'data-focused:border-border-focus data-focus-visible:outline-3 data-focus-visible:outline-border-focus',
          'data-invalid:border-feedback-danger',
          size === 'lg' ? 'min-h-touch-target text-lg font-semibold' : 'min-h-touch-min text-md',
        )}
      />
      {description && (
        <AriaText slot="description" className="text-sm text-text-muted">
          {description}
        </AriaText>
      )}
      <FieldError className="text-sm font-semibold text-feedback-danger">{errorMessage}</FieldError>
    </AriaTextField>
  );
}
